import type { RealTimeMessage } from './types';

type MessageHandler = (message: RealTimeMessage) => void;
type ConnectionHandler = (isConnected: boolean) => void;

interface SignalRClient {
  id: string;
  flatId: number;
  onMessage: MessageHandler;
  onConnectionStateChanged: ConnectionHandler;
}

/**
 * Mock SignalR Server
 * Имитирует работу SignalR хаба для real-time обновлений
 */
class MockSignalRServer {
  private clients: Map<string, SignalRClient> = new Map();
  private isRunning = false;
  private simulationInterval: ReturnType<typeof setInterval> | null = null;
  
  // Вероятность случайного события (0-1)
  private eventProbability = 0.1;

  start() {
    if (this.isRunning) return;
    
    this.isRunning = true;
    
    // Запускаем симуляцию событий каждые 3-8 секунд
    this.simulationInterval = setInterval(() => {
      this.simulateRandomEvent();
    }, 3000 + Math.random() * 5000);
    
    console.log('[MockSignalR] Server started');
  }

  stop() {
    this.isRunning = false;
    
    if (this.simulationInterval) {
      clearInterval(this.simulationInterval);
      this.simulationInterval = null;
    }
    
    this.clients.clear();
    console.log('[MockSignalR] Server stopped');
  }

  connect(
    flatId: number,
    onMessage: MessageHandler,
    onConnectionStateChanged: ConnectionHandler
  ): string {
    const clientId = Math.random().toString(36).substring(2, 15);
    
    const client: SignalRClient = {
      id: clientId,
      flatId,
      onMessage,
      onConnectionStateChanged
    };
    
    this.clients.set(clientId, client);
    
    // Сообщаем о подключении
    setTimeout(() => {
      onConnectionStateChanged(true);
      console.log(`[MockSignalR] Client ${clientId} connected to flat ${flatId}`);
    }, 500);
    
    return clientId;
  }

  disconnect(clientId: string) {
    const client = this.clients.get(clientId);
    if (client) {
      client.onConnectionStateChanged(false);
      this.clients.delete(clientId);
      console.log(`[MockSignalR] Client ${clientId} disconnected`);
    }
  }

  /**
   * Отправить сообщение всем клиентам определенной квартиры
   */
  broadcastToFlat(flatId: number, message: RealTimeMessage) {
    this.clients.forEach(client => {
      if (client.flatId === flatId) {
        setTimeout(() => {
          client.onMessage(message);
        }, 100 + Math.random() * 200);
      }
    });
  }

  /**
   * Отправить сообщение конкретному клиенту
   */
  sendToClient(clientId: string, message: RealTimeMessage) {
    const client = this.clients.get(clientId);
    if (client) {
      setTimeout(() => {
        client.onMessage(message);
      }, 100);
    }
  }

  /**
   * Симуляция случайных событий от устройств
   */
  private simulateRandomEvent() {
    if (!this.isRunning || this.clients.size === 0) return;
    
    if (Math.random() > this.eventProbability) return;

    const flatIds = [...new Set([...this.clients.values()].map(c => c.flatId))];
    const randomFlatId = flatIds[Math.floor(Math.random() * flatIds.length)];

    const eventTypes = ['deviceUpdate', 'deviceUpdate', 'deviceUpdate', 'safetyAlert'];
    const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];

    switch (eventType) {
      case 'deviceUpdate':
        this.simulateDeviceUpdate(randomFlatId);
        break;
      case 'safetyAlert':
        this.simulateSafetyAlert(randomFlatId);
        break;
    }
  }

  private simulateDeviceUpdate(flatId: number) {
    const devices = [
      { id: 'device_001', name: 'Лампа в гостиной' },
      { id: 'device_002', name: 'Термостат' },
      { id: 'device_003', name: 'Датчик движения' }
    ];
    
    const randomDevice = devices[Math.floor(Math.random() * devices.length)];
    const isMotionSensor = randomDevice.id === 'device_003';
    
    if (isMotionSensor) {
      // Датчик движения - случайное движение
      const hasMotion = Math.random() > 0.5;
      
      this.broadcastToFlat(flatId, {
        type: 'deviceUpdate',
        deviceId: randomDevice.id,
        parameter: 'motion',
        value: hasMotion,
        timestamp: Date.now(),
        message: hasMotion ? 'Обнаружено движение в коридоре' : 'Движение прекратилось'
      });
    } else {
      // Обычное устройство - случайное изменение параметра
      this.broadcastToFlat(flatId, {
        type: 'deviceUpdate',
        deviceId: randomDevice.id,
        parameter: 'power',
        value: Math.random() > 0.5,
        timestamp: Date.now()
      });
    }
    
    console.log(`[MockSignalR] Device update: ${randomDevice.name}`);
  }

  private simulateSafetyAlert(flatId: number) {
    const alerts = [
      { type: 'motion', message: 'Движение в зоне охраны!' },
      { type: 'door', message: 'Входная дверь открыта' }
    ];
    
    const alert = alerts[Math.floor(Math.random() * alerts.length)];
    
    this.broadcastToFlat(flatId, {
      type: 'safetyAlert',
      alertType: alert.type,
      message: alert.message,
      timestamp: Date.now()
    });
    
    console.log(`[MockSignalR] Safety alert: ${alert.message}`);
  }

  /**
   * Триггер сценария
   */
  triggerScript(flatId: number, scriptId: string, scriptName: string) {
    this.broadcastToFlat(flatId, {
      type: 'scriptTriggered',
      deviceId: scriptId,
      message: `Сценарий "${scriptName}" запущен`,
      timestamp: Date.now()
    });
  }

  /**
   * Обновление статуса подключения устройства
   */
  deviceConnectionStatus(deviceId: string, isOnline: boolean) {
    this.clients.forEach(client => {
      setTimeout(() => {
        client.onMessage({
          type: 'deviceUpdate',
          deviceId,
          parameter: 'connectionStatus',
          value: isOnline,
          timestamp: Date.now()
        });
      }, 100);
    });
  }

  getConnectedClientsCount(): number {
    return this.clients.size;
  }

  getConnectedFlats(): number[] {
    return [...new Set([...this.clients.values()].map(c => c.flatId))];
  }
}

// Экспортируем синглтон
export const mockSignalRServer = new MockSignalRServer();
export default mockSignalRServer;
